import {IHttpPromise, IHttpResponse, IHttpService, IPromise} from "angular";
import {getAbsoluteUrl} from "../../utils/urlUtill";
import {LqbUpdateDTO, UserDto} from "../../users/model/User";
import {Subject} from "rxjs";
import {filter} from "rxjs/operators";
import {Page} from "../../utils/models/Page";
import {Branch} from "../../branches/model/Branch";
import {BranchUsers} from "../../branches/model/BranchUsers";
import {UserAutocompleteDTO} from "../model/UserAutocompleteDTO";
import {UserFilter, UserView} from "../../users/model/UserFilter";
import {FeatureAccessLevel} from "../../users/model/AccessLevelToggle";
import {ToastNotificationService} from "../../utils/ToastNotificationService";
import {Customer} from "../../customers/model/Customer";
import {Borrower} from "../../customers/model/Borrower";

export enum UserEventType {
    CREATED, UPDATED, DELETED
}

export interface UserEvent {
    type: UserEventType;
    value: UserDto;
}

export class UserService {

    public static instance = ['$http', UserService];
    public userUpdatedSubject: Subject<UserEvent> = new Subject();

    constructor(private $http: IHttpService,
                private notificationSerivice: ToastNotificationService) {
        this.$http = $http;
    }

    subscribeForUserEventsWithType = (type: UserEventType, observer: (next: UserEvent) => void) => {
        this.userUpdatedSubject
            .pipe(filter(event => event.type === type))
            .subscribe(observer);
    };

    subscribeForUserWithIdEventsWithType = (userId: number, type: UserEventType, observer: (next: UserEvent) => void) => {
        this.userUpdatedSubject
            .pipe(filter(event => event.value.id === userId && event.type === type))
            .subscribe(observer);
    };

    //todo fix autocomplete
    queryEmployee = (userFilter: UserFilter = ({
        userIds: [],
        branchesIds: [],
        showDisabled: false,
        showDeleted: false,
        view: UserView.AUTOCOMPLETE,
        query: ''
    })): IHttpPromise<UserDto[]> => {
        return this.$http({
            method: 'GET',
            url: getAbsoluteUrl('api/users/filtered'),
            params: userFilter
        })
    };

    changeCustomerOriginalLoanOfficer = (employee: UserDto,
                                         customer: Borrower) => {
        return this.$http({
            method: 'POST',
            url: getAbsoluteUrl(`api/employee/customers?new_original_lo_id=${employee.id}&customer_id=${customer.id}`)
        })
    };

    transferCustomersToEmployee = (employee: UserDto,
                                   customer: Customer) => {
        return this.$http({
            method: 'POST',
            url: getAbsoluteUrl(`api/employee/customers/lo?transfer_to_id=${employee.id}&customer_id=${customer.id}`)
        })
    };

    transferCustomersFromUserToUser = (user1: UserDto,
                                       user2: UserDto) => {
        return this.$http({
            method: 'POST',
            url: getAbsoluteUrl(`api/employee/customers?transfer_to_id=${user2.id}&transfer_from_email=${user1.email}`)
        })
    };

    getLqbUsers = (): IHttpPromise<LqbUpdateDTO[]> => this.$http({method: 'GET', url: getAbsoluteUrl('api/users/lqb')});

    subscribeForUserEvents = (user: UserDto, observer: (next: UserEvent) => void) => {
        this.userUpdatedSubject
            .subscribe(observer);
    };


    subscribeForUserWithIdEvents = (userId: number, observer: (next: UserEvent) => void) => {
        this.userUpdatedSubject
            .pipe(filter(event => event.value.id === userId))
            .subscribe(observer);
    };

    getActiveUsersOfTheBranch = (branch: Branch): IHttpPromise<BranchUsers[]> => {
        return this.$http({
            method: 'GET',
            url: getAbsoluteUrl(`api/branches/${branch.branchName}/employees`),
            params: {
                includeInactive: false
            }
        });
    };

    getAllUsersOfTheBranch = (branch: Branch): IHttpPromise<BranchUsers[]> =>
        this.$http({
            method: 'GET',
            url: getAbsoluteUrl(`api/branches/${branch.branchName}/employees`),
            params: {
                includeInactive: true
            }
        });

    userChanged = (type: UserEventType, user: UserDto) => {
        this.userUpdatedSubject.next({value: user, type: type});
    };

    getAllUsers = (params?: any): IPromise<IHttpResponse<UserDto[]>> =>
        this.$http({
            method: 'GET',
            url: getAbsoluteUrl("api/users/"),
            params: params
        });


    getAllUsersForAutocompleate = (): IPromise<IHttpResponse<UserAutocompleteDTO[]>> => {
        return this.$http({
            method: 'GET',
            url: getAbsoluteUrl("api/users/"),
            params: {"format_autocomplete": true}
        });
    };

    updateUserAvatar = (avatarId: number, userId: number) => this.$http({
        method: 'PUT',
        data: {id: avatarId},
        url: getAbsoluteUrl(`api/users/${userId}/avatar`)
    });

    updateUser = (user: UserDto) => this.$http({
        method: 'PUT',
        url: getAbsoluteUrl('api/users'),
        data: user
    }).then(resp => {
        if (resp.status == 200) {
            this.userChanged(UserEventType.UPDATED, resp.data);
        }
        return resp;
    });


    saveUser = (user: UserDto): IHttpPromise<UserDto> => {
        return this.$http({
            method: 'POST',
            url: getAbsoluteUrl('api/users'),
            data: user
        }).then(resp => {
            if (resp.status == 200) {
                this.userChanged(UserEventType.CREATED, resp.data);
            }
            return resp;
        });
    };


    getUserPermissions = (id: number): IHttpPromise<FeatureAccessLevel[]> => this.$http({
        method: 'GET',
        url: getAbsoluteUrl(`api/users/${id}/permissions`)
    });


    restoreUser = (user: UserDto): IPromise<IHttpResponse<void>> =>
        this.$http({
            method: 'PUT',
            url: getAbsoluteUrl(`api/users/${user.id}?restore=true`)
        });

    deleteUser = (user: UserDto) =>
        this.$http({
            method: 'DELETE',
            url: getAbsoluteUrl(`api/users/${user.id}`),
            params: {
                permanently: false
            }
        }).then(resp => {
            if (resp.status === 200) {
                this.userChanged(UserEventType.DELETED, user);
            }
            return resp;
        });

    getUsersPage = (pageNumber: number, pageSize: number): IHttpPromise<Page<UserDto>> => this.$http({
        method: 'GET',
        url: getAbsoluteUrl('api/users'),
        params: {
            pageNumber: pageNumber,
            pageSize: pageSize
        }
    });

    deactivateUser = (id: number) => this.updateUserStatus(id, false);

    updateUserStatus = (id: number,
                        status: boolean) => this.$http({
        method: 'PUT',
        url: getAbsoluteUrl(`api/users/${id}/status`),
        data: {
            isActive: status
        }
    });

    getFilteredUsers = (filter: UserFilter, page: number, size: number): IHttpPromise<Page<UserDto>> => {
        return this.$http({
            method: 'GET',
            url: getAbsoluteUrl('api/users/filtered'),
            params: Object.assign({pageNumber: page, pageSize: size}, filter)
        })
    };

    morphIntoUser = (id: number) =>
        this.$http({
            method: 'POST',
            url: getAbsoluteUrl(`api/users/${id}/morph`)
        });

    sendWelcomeEmail = (userId: number): IHttpPromise<any> => {
        return this.$http({
            method: 'POST',
            url: getAbsoluteUrl(`api/users/${userId}/send_welcome_email`)
        });
    };

    sendResetPasswordEmail = (userId: number): IHttpPromise<any> => {
        return this.$http({
            method: 'POST',
            url: getAbsoluteUrl(`api/users/${userId}/send_reset_password_email`)
        });
    };

    deleteUserPermenantly = (user: UserDto) => this.$http({
        method: 'DELETE',
        url: getAbsoluteUrl(`api/users/${user.id}`),
        params: {
            permanently: true
        }
    });

    updateUsersBranch = (users: BranchUsers[], branchName: string) => {
        return this.$http({
            method: 'PUT',
            url: getAbsoluteUrl('api/users/branch'),
            data: {
                users: users,
                branchName: branchName
            }
        })
    };

    getUserViewById(id: number, view: UserView) {
        return this.$http({
            method: 'GET',
            url: getAbsoluteUrl(`api/users/${id}`),
            params: {
                view: view
            }
        })
    }

    toggleSuperAdmin(user: UserDto) : IHttpPromise<any> {
        return this.$http({
            method: 'PUT',
            url: getAbsoluteUrl(`api/users/${user.id}/toggle_super_admin`)
        })
    }
}